import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { JsonPatchOperation } from '@core/data/models/json-patch.model';
import { ResultPage } from '@core/data/models/result-page.model';
import { mapResponseWithLocationId } from '@utils/location-header.util';
import { environment } from 'src/environments/environment';
import { TextBlock } from '../models/text-block.model';

export interface TextBlockRequestParams {
  search?: string;
  textBlockCategoryIds?: number[];
  textBlockAreaIds?: number[];
}

@Injectable({
  providedIn: 'root',
})
export class TextBlockService {
  private readonly http = inject(HttpClient);
  protected apiUrl = `${environment.apiUrl}/text-block-templates`;

  loadList(): Observable<TextBlock[]> {
    const httpParams = new HttpParams()
      .set('page', '1')
      .set('pageSize', '5000');

    return this.http
      .get<ResultPage<TextBlock>>(`${this.apiUrl}`, { params: httpParams })
      .pipe(map((response) => response.result));
  }

  getListWithParams(params: TextBlockRequestParams): Observable<TextBlock[]> {
    let httpParams = new HttpParams().set('page', '1').set('pageSize', '5000');

    if (params.search) {
      httpParams = httpParams.set('textBlockSearch', params.search);
    }

    if (params.textBlockCategoryIds?.length) {
      httpParams = httpParams.set(
        'textBlockCategoryId_in',
        params.textBlockCategoryIds.join(',')
      );
    }

    if (params.textBlockAreaIds?.length) {
      httpParams = httpParams.set(
        'textBlockAreaId_in',
        params.textBlockAreaIds.join(',')
      );
    }

    return this.http
      .get<ResultPage<TextBlock>>(this.apiUrl, { params: httpParams })
      .pipe(map((response) => response.result));
  }

  getById(id: string): Observable<TextBlock> {
    return this.http.get<TextBlock>(`${this.apiUrl}/${id}`);
  }

  create(category: Partial<TextBlock>): Observable<TextBlock> {
    return this.http
      .post<number>(`${this.apiUrl}`, category, {
        observe: 'response',
      })
      .pipe(
        map((response) =>
          mapResponseWithLocationId(response, category as TextBlock)
        )
      );
  }

  update(id: number, operations: JsonPatchOperation[]): Observable<TextBlock> {
    return this.http.patch<TextBlock>(`${this.apiUrl}/${id}`, operations);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }
}
